import React from 'react';
import { Sparkles } from 'lucide-react';
import ResumePreview from '../../../components/ResumePreview';
import { usePreviewContext } from '../providers/PreviewProvider';
import { PageConstraints } from '../../../design-system/constraints/pageConstraints';

export default function ResumeCanvas() {
  const {
    resumeData,
    template,
    accentColor,
    fontFamily,
    fontScale,
    editMode,
    transitioning,
    shouldFlash,
    resumeInnerRef,
    effectiveZoom,
    activeVariantId,
    handleFieldUpdate,
    handleDeleteBullet,
    handleDeleteSkill,
  } = usePreviewContext();

  // ── SSOT — what does the canvas actually receive? ──
  const effectiveResumeData = resumeData;
  const canvasWidth = PageConstraints?.A4_WIDTH || 794;
  const canvasMinHeight = PageConstraints?.A4_HEIGHT || 1123;

  return (
    <>
      {/* Sample preview state — shown before user has a resume */}
      {!resumeData && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" style={{ minHeight: '70vh', fontFamily: "'Inter', sans-serif" }}>
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 20,
            padding: '40px 32px',
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backdropFilter: 'blur(8px)',
          }}>
            {/* Pulsing Beacon */}
            <div style={{ position: 'relative', width: 48, height: 48, marginBottom: 20 }}>
              <div className="absolute inset-0 rounded-full bg-sky-500/20 animate-ping" />
              <div className="absolute inset-2 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400">
                <Sparkles size={18} />
              </div>
            </div>

            <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Live Preview
            </span>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginTop: 8, marginBottom: 12 }}>
              Your Resume Workspace
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: 24 }}>
              Your formatted resume will render here. As you chat with your AI Coach, your template styling, sections, and bullet points will update in real-time.
            </p>

            <div style={{ width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.01)', border: '1px solid var(--card-border)', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12 }}>💬</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-primary)', fontWeight: 600 }}>Chat with AI to generate sections</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12 }}>📤</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-primary)', fontWeight: 600 }}>Import existing file to optimize</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12 }}>🎨</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-primary)', fontWeight: 600 }}>Switch templates and colors instantly</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live resume */}
      {resumeData && (
        <div
          style={{
            width: `${canvasWidth * effectiveZoom}px`,
            minHeight: `${Math.max(canvasMinHeight, (resumeInnerRef.current?.scrollHeight || canvasMinHeight)) * effectiveZoom}px`,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {/* ── [TRACE BUG2:ResumeCanvas] effectiveZoom visible to PageEngine ── */}
          {typeof window !== 'undefined' && console.log(
            `[TRACE BUG2:ResumeCanvas] effectiveZoom=${effectiveZoom} canvasW=${canvasWidth} canvasMinH=${canvasMinHeight}`
          )}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${canvasWidth}px`,
              transform: `scale(${effectiveZoom})`,
              transformOrigin: 'top left',
            }}
          >
            <div
              ref={resumeInnerRef}
              className={`bg-white rounded-xl shadow-2xl shadow-black/30 transition-opacity duration-200 ${transitioning ? 'opacity-0' : 'opacity-100'} ${shouldFlash ? 'animate-flash-success' : ''}`}
            >
              <ResumePreview
                resumeData={resumeData}
                template={template}
                accentColor={accentColor}
                fontFamily={fontFamily}
                fontScale={fontScale}
                editMode={editMode}
                showPageBoundaries={true}
                onFieldUpdate={handleFieldUpdate}
                onDeleteBullet={handleDeleteBullet}
                onDeleteSkill={handleDeleteSkill}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
